import { useCallback, useEffect, useRef, useState } from 'react';

export type DragMode = 'paint' | 'clear';
export type CommitPhase = 'preview' | 'persist';
export type DragActivation = 'immediate' | 'longPress';

export interface SlotKey {
  day: string;
  hour: number;
}

interface UseSlotDragOpts {
  isSelected: (day: string, hour: number) => boolean;
  onCommit: (mode: DragMode, slots: SlotKey[], phase: CommitPhase) => void;
  isSlotDisabled?: (day: string, hour: number) => boolean;
  activation?: DragActivation;
  longPressMs?: number;
  onDrawModeChange?: (active: boolean) => void;
}

const MOVE_THRESHOLD = 6;
const GESTURE_CANCEL_THRESHOLD = 14;
const PAINT_SAMPLE_STEP = 10;
const DEFAULT_LONG_PRESS_MS = 400;

function resolveSlot(target: EventTarget | null): SlotKey | null {
  if (!(target instanceof Element)) return null;
  if (target.closest('[data-slot-count-badge]')) return null;
  const el = target.closest<HTMLElement>('[data-slot-day][data-slot-hour]');
  if (!el) return null;
  const day = el.dataset.slotDay;
  const hourStr = el.dataset.slotHour;
  if (!day || !hourStr) return null;
  const hour = Number(hourStr);
  if (!Number.isFinite(hour)) return null;
  return { day, hour };
}

function resolveSlotAtPoint(x: number, y: number): SlotKey | null {
  const el = document.elementFromPoint(x, y);
  return resolveSlot(el);
}

function isVerticalScrollIntent(dx: number, dy: number) {
  return Math.abs(dy) >= GESTURE_CANCEL_THRESHOLD && Math.abs(dy) > Math.abs(dx);
}

function isHorizontalNavIntent(dx: number, dy: number) {
  return Math.abs(dx) >= GESTURE_CANCEL_THRESHOLD && Math.abs(dx) > Math.abs(dy);
}

function isGestureIntent(dx: number, dy: number) {
  return isVerticalScrollIntent(dx, dy) || isHorizontalNavIntent(dx, dy);
}

function collectSlotsAlongSegment(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  isSlotDisabled?: (day: string, hour: number) => boolean
): SlotKey[] {
  const dist = Math.hypot(x1 - x0, y1 - y0);
  const steps = Math.max(1, Math.ceil(dist / PAINT_SAMPLE_STEP));
  const seen = new Map<string, SlotKey>();

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const slot = resolveSlotAtPoint(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    if (!slot || isSlotDisabled?.(slot.day, slot.hour)) continue;
    seen.set(`${slot.day}-${slot.hour}`, slot);
  }

  return Array.from(seen.values());
}

export function useSlotDrag({
  isSelected,
  onCommit,
  isSlotDisabled,
  activation = 'immediate',
  longPressMs = DEFAULT_LONG_PRESS_MS,
  onDrawModeChange,
}: UseSlotDragOpts) {
  const [isDrawMode, setIsDrawMode] = useState(false);
  const onDrawModeChangeRef = useRef(onDrawModeChange);
  onDrawModeChangeRef.current = onDrawModeChange;

  const setDrawMode = useCallback((active: boolean) => {
    setIsDrawMode(active);
    onDrawModeChangeRef.current?.(active);
  }, []);
  const longPressTimerRef = useRef<number | null>(null);
  const captureTargetRef = useRef<Element | null>(null);
  const touchBlockElRef = useRef<Element | null>(null);
  const touchMoveBlockerRef = useRef<((e: TouchEvent) => void) | null>(null);

  const activeRef = useRef<{
    mode: DragMode | null;
    started: boolean;
    activated: boolean;
    pointerId: number | null;
    startX: number;
    startY: number;
    lastPaintX: number | null;
    lastPaintY: number | null;
    visited: Map<string, SlotKey>;
    anchor: SlotKey | null;
  }>({
    mode: null,
    started: false,
    activated: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    lastPaintX: null,
    lastPaintY: null,
    visited: new Map(),
    anchor: null,
  });

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const detachTouchBlocker = useCallback(() => {
    const el = touchBlockElRef.current;
    const fn = touchMoveBlockerRef.current;
    if (el && fn) el.removeEventListener('touchmove', fn);
    touchBlockElRef.current = null;
    touchMoveBlockerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    clearLongPressTimer();
    detachTouchBlocker();
    captureTargetRef.current = null;
    activeRef.current = {
      mode: null,
      started: false,
      activated: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      lastPaintX: null,
      lastPaintY: null,
      visited: new Map(),
      anchor: null,
    };
    setDrawMode(false);
  }, [clearLongPressTimer, detachTouchBlocker, setDrawMode]);

  const abandonPending = useCallback(() => {
    clearLongPressTimer();
    detachTouchBlocker();
    activeRef.current = {
      mode: null,
      started: false,
      activated: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      lastPaintX: null,
      lastPaintY: null,
      visited: new Map(),
      anchor: null,
    };
    captureTargetRef.current = null;
  }, [clearLongPressTimer, detachTouchBlocker]);

  const paintSlotsBetween = useCallback(
    (x0: number, y0: number, x1: number, y1: number) => {
      const a = activeRef.current;
      if (!a.mode) return;

      const slots = collectSlotsAlongSegment(x0, y0, x1, y1, isSlotDisabled);
      if (slots.length === 0) return;

      if (!a.started) {
        const dx = x1 - a.startX;
        const dy = y1 - a.startY;
        if (dx * dx + dy * dy < MOVE_THRESHOLD * MOVE_THRESHOLD) return;
        a.started = true;
      }

      for (const slot of slots) {
        const key = `${slot.day}-${slot.hour}`;
        if (!a.visited.has(key)) {
          a.visited.set(key, slot);
          onCommit(a.mode, [slot], 'preview');
        }
      }
    },
    [isSlotDisabled, onCommit]
  );

  const attachPendingScrollWatch = useCallback(() => {
    const el = captureTargetRef.current;
    if (!el) return;

    detachTouchBlocker();

    const onTouchMove = (e: TouchEvent) => {
      const a = activeRef.current;
      if (!a.mode || a.activated) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - a.startX;
      const dy = touch.clientY - a.startY;
      if (isGestureIntent(dx, dy)) {
        abandonPending();
      }
    };

    el.addEventListener('touchmove', onTouchMove, { passive: true });
    touchBlockElRef.current = el;
    touchMoveBlockerRef.current = onTouchMove;
  }, [abandonPending, detachTouchBlocker]);

  const attachDrawModeTouchBlocker = useCallback(() => {
    const el = captureTargetRef.current;
    if (!el) return;

    detachTouchBlocker();

    const onTouchMove = (e: TouchEvent) => {
      if (activeRef.current.activated) {
        e.preventDefault();
      }
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    touchBlockElRef.current = el;
    touchMoveBlockerRef.current = onTouchMove;
  }, [detachTouchBlocker]);

  const activateLongPress = useCallback(
    (pointerId: number) => {
      const a = activeRef.current;
      if (!a.mode || !a.anchor || a.activated) return;
      const dx = (a.lastPaintX ?? a.startX) - a.startX;
      const dy = (a.lastPaintY ?? a.startY) - a.startY;
      if (isGestureIntent(dx, dy)) {
        abandonPending();
        return;
      }
      a.activated = true;
      a.lastPaintX = a.startX;
      a.lastPaintY = a.startY;
      try {
        captureTargetRef.current?.setPointerCapture?.(pointerId);
      } catch {
        /* ignore */
      }
      setDrawMode(true);
      navigator.vibrate?.(10);
      attachDrawModeTouchBlocker();
      onCommit(a.mode, [a.anchor], 'preview');
    },
    [abandonPending, attachDrawModeTouchBlocker, onCommit, setDrawMode]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== undefined && e.button !== 0) return;
      const slot = resolveSlot(e.target);
      if (!slot) return;
      if (isSlotDisabled?.(slot.day, slot.hour)) return;
      const wasSelected = isSelected(slot.day, slot.hour);
      const mode: DragMode = wasSelected ? 'clear' : 'paint';
      const visited = new Map<string, SlotKey>();
      visited.set(`${slot.day}-${slot.hour}`, slot);

      if (activation === 'longPress') {
        captureTargetRef.current = e.currentTarget as Element;
        activeRef.current = {
          mode,
          started: false,
          activated: false,
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          lastPaintX: null,
          lastPaintY: null,
          visited,
          anchor: slot,
        };
        clearLongPressTimer();
        attachPendingScrollWatch();
        const pointerId = e.pointerId;
        longPressTimerRef.current = window.setTimeout(() => {
          longPressTimerRef.current = null;
          if (activeRef.current.pointerId === pointerId) {
            activateLongPress(pointerId);
          }
        }, longPressMs);
        return;
      }

      activeRef.current = {
        mode,
        started: false,
        activated: true,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        lastPaintX: e.clientX,
        lastPaintY: e.clientY,
        visited,
        anchor: slot,
      };
      try {
        (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onCommit(mode, [slot], 'preview');
    },
    [
      activation,
      activateLongPress,
      attachPendingScrollWatch,
      clearLongPressTimer,
      isSelected,
      isSlotDisabled,
      longPressMs,
      onCommit,
    ]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const a = activeRef.current;
      if (!a.mode || a.pointerId !== e.pointerId) return;

      if (activation === 'longPress' && !a.activated) {
        const dx = e.clientX - a.startX;
        const dy = e.clientY - a.startY;
        if (isGestureIntent(dx, dy)) {
          abandonPending();
        }
        return;
      }

      const lastX = a.lastPaintX ?? a.startX;
      const lastY = a.lastPaintY ?? a.startY;
      paintSlotsBetween(lastX, lastY, e.clientX, e.clientY);
      a.lastPaintX = e.clientX;
      a.lastPaintY = e.clientY;
    },
    [abandonPending, activation, paintSlotsBetween]
  );

  const finish = useCallback(
    (committed: boolean, end?: { x: number; y: number }) => {
      const a = activeRef.current;
      if (!a.mode) {
        reset();
        return;
      }

      if (activation === 'longPress' && !a.activated) {
        clearLongPressTimer();
        detachTouchBlocker();
        if (committed && a.anchor && end) {
          const dx = end.x - a.startX;
          const dy = end.y - a.startY;
          if (!isGestureIntent(dx, dy)) {
            onCommit(a.mode, [a.anchor], 'persist');
          }
        }
        reset();
        return;
      }

      if (!committed) {
        const revertMode: DragMode = a.mode === 'paint' ? 'clear' : 'paint';
        const slots = Array.from(a.visited.values());
        if (slots.length > 0) onCommit(revertMode, slots, 'preview');
        reset();
        return;
      }
      if (!a.started) {
        if (a.anchor) onCommit(a.mode, [a.anchor], 'persist');
      } else {
        const slots = Array.from(a.visited.values());
        if (slots.length > 0) onCommit(a.mode, slots, 'persist');
      }
      reset();
    },
    [activation, clearLongPressTimer, detachTouchBlocker, onCommit, reset]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (activeRef.current.pointerId !== e.pointerId) return;
      finish(true, { x: e.clientX, y: e.clientY });
    },
    [finish]
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      if (activeRef.current.pointerId !== e.pointerId) return;
      finish(false);
    },
    [finish]
  );

  useEffect(() => {
    const onWindowUp = () => {
      if (activeRef.current.mode) finish(true);
    };
    window.addEventListener('pointerup', onWindowUp);
    window.addEventListener('pointercancel', onWindowUp);
    return () => {
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
    };
  }, [finish]);

  useEffect(
    () => () => {
      onDrawModeChangeRef.current?.(false);
      clearLongPressTimer();
      detachTouchBlocker();
    },
    [clearLongPressTimer, detachTouchBlocker]
  );

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    isDrawMode,
    isDragging: () => activeRef.current.started,
  };
}
